import { useCallback, useEffect, useRef, useState } from 'react';
import { ROXY_STATES } from './roxyStates';

const GREETING_MS = 900;
const SPEAKING_MS = 1600;
const SUCCESS_MS = 1600;
const ALERT_MS = 2400;
// thinking is a short beat; past this she is on a longer job
const WORKING_AFTER_MS = 6000;

/**
 * Maps the chat's own state onto Roxy's nine. `handoff` has no trigger yet —
 * the backend does not signal escalation to a human, so nothing emits it.
 *
 * Returns the current state plus `pulse`, which the drawer calls to flash a
 * one-shot state (success on a clean end, alert on a failed send).
 */
const useRoxyState = ({ open, loading, draft, messages, isLimitReached }) => {
    const [transient, setTransient] = useState(null);
    const [longRun, setLongRun] = useState(false);
    const timerRef = useRef(null);

    const pulse = useCallback((next, ms) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setTransient(next);
        timerRef.current = setTimeout(() => {
            setTransient(null);
            timerRef.current = null;
        }, ms);
    }, []);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        if (open) pulse(ROXY_STATES.GREETING, GREETING_MS);
        else setTransient(null);
    }, [open, pulse]);

    useEffect(() => {
        if (!loading) {
            setLongRun(false);
            return undefined;
        }
        const t = setTimeout(() => setLongRun(true), WORKING_AFTER_MS);
        return () => clearTimeout(t);
    }, [loading]);

    const agentCount = messages.filter((m) => m.role === 'agent').length;
    const prevAgentCount = useRef(agentCount);
    useEffect(() => {
        if (agentCount > prevAgentCount.current) pulse(ROXY_STATES.SPEAKING, SPEAKING_MS);
        prevAgentCount.current = agentCount;
    }, [agentCount, pulse]);

    let state = ROXY_STATES.IDLE;
    if (loading) state = longRun ? ROXY_STATES.WORKING : ROXY_STATES.THINKING;
    else if (transient) state = transient;
    else if (isLimitReached) state = ROXY_STATES.ALERT;
    else if (draft.trim()) state = ROXY_STATES.LISTENING;

    return {
        state,
        pulseSuccess: useCallback(() => pulse(ROXY_STATES.SUCCESS, SUCCESS_MS), [pulse]),
        pulseAlert: useCallback(() => pulse(ROXY_STATES.ALERT, ALERT_MS), [pulse]),
    };
};

export default useRoxyState;
